import type { Metric } from "@/entities/Finance/Finance.model";
import { useMemo, useState } from "react";
import { VariableIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { InfoTooltip } from "./InfoTooltip";

interface MetricInfoDisplayProps {
    metric: Metric;
}

const COMPOSITION_DESCRIPTIONS: Record<string, string> = {
    derived:
        "A field calculated or generated from one or more base fields using logic or formulas.",
    base: "A primary, raw data field stored directly in the system (entered or ingested as-is).",
    raw: "A primary, raw data field stored directly in the system (entered or ingested as-is).",
};

export function MetricInfoDisplay({ metric }: MetricInfoDisplayProps) {
    const [showFormula, setShowFormula] = useState(false);

    const compositionDisplay = useMemo(() => {
        const composition = metric.description?.composition;
        if (!composition) return undefined;
        return composition.toLowerCase() === "raw" ? "base" : composition;
    }, [metric.description?.composition]);

    const description = useMemo(() => {
        const fullDescription = metric.description?.description;
        if (!fullDescription) return undefined;
        const parts = fullDescription.split("\n\n");
        const out = parts.length >= 3 ? parts[1] : fullDescription;
        return out;
    }, [metric.description?.description]);

    const formula = useMemo(() => {
        const fullDescription = metric.description?.description;

        if (!fullDescription) return [];

        const afterExpression = fullDescription.split(
            "With the expression:\n",
        )[1];
        if (!afterExpression) return [];

        const codeBlockMatch = afterExpression.match(/```text\n([\s\S]*?)```/);
        if (!codeBlockMatch) return [];

        const formulaString = codeBlockMatch[1];
        const functionMatch = formulaString.match(/(\w+)\s*\(([\s\S]*)\)/);

        if (!functionMatch) {
            return [];
        }

        const [, , innerContent] = functionMatch;

        return innerContent
            .split(",")
            .map((operand) => operand.trim())
            .filter((operand) => operand && operand !== "");
    }, [metric.description?.description]);

    return (
        <div className="flex flex-col gap-4">
            {description && (
                <p className="body-s italic text-gray-400">{description}</p>
            )}

            {compositionDisplay && (
                <p className="body-s flex items-center gap-1 text-gray-400">
                    <span className="capitalize italic">
                        {compositionDisplay}
                    </span>
                    <InfoTooltip>
                        {COMPOSITION_DESCRIPTIONS[
                            compositionDisplay.toLowerCase()
                        ] || compositionDisplay}
                    </InfoTooltip>
                </p>
            )}

            {formula.length > 0 && (
                <div className="flex flex-col gap-2">
                    <p
                        className={clsx(
                            "body-s subdued flex cursor-pointer items-center gap-1",
                        )}
                        onClick={() => setShowFormula(!showFormula)}
                    >
                        {showFormula ? "Hide formula" : "Show formula"}{" "}
                        <VariableIcon className="size-4" />
                    </p>
                    {showFormula && (
                        <div
                            className={clsx(
                                "bg-[#263142] p-5",
                                "flex flex-wrap",
                                "gap-2 rounded",
                            )}
                        >
                            {formula.map((line, idx) => {
                                return (
                                    <p
                                        className="py-1 font-[300]"
                                        key={`${line}-${idx}`}
                                    >
                                        {line +
                                            (line ===
                                            formula[formula.length - 1]
                                                ? ""
                                                : " +")}
                                    </p>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
